import { el } from '../state/store.js';
import { vlCreateVideo, vlUploadUrl } from '../services/video-links.js';
import { clock, playButton, titleFromFilename } from '../core/video-links.js';
import { toast } from './toast.js';

/* THE UPLOAD FORM for a video and its thumbnail.

   The browser sends both files straight to the media bucket, to a short-lived
   address the video-links-admin function signs for it (supabase/functions/
   video-links-admin/r2.ts) — the bytes never pass through a server of ours and
   no credential is ever in the page. Choosing the MP4 reads its length from the
   file itself; the thumbnail is either a frame picked from the video, with the
   play button drawn on it, or a picture of your own.

   Only shown when the function says uploads are set up; otherwise the Admin tab
   keeps its fields for paths typed by hand. */

/** One PUT with progress, cancellable. Resolves on a 2xx, rejects with a readable reason. */
export function putFile(url, headers, blob, onProgress, signal) {
  return new Promise((ok, no) => {
    const xhr = new window.XMLHttpRequest();
    xhr.open('PUT', url);
    for (const [k, v] of Object.entries(headers || {})) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = e => { if (e.lengthComputable) onProgress(e.loaded / e.total); };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300) ? ok()
      : no(new Error(xhr.status === 403
          ? 'The storage refused the upload (403). Check the bucket’s CORS policy allows PUT from this address, and that the R2 token is for this bucket.'
          : `The upload failed (${xhr.status}).`));
    xhr.onerror = () => no(new Error('The upload could not reach the storage. Check the bucket’s CORS policy allows PUT from this address.'));
    xhr.onabort = () => no(new Error('Cancelled.'));
    if (signal) signal.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(blob);
  });
}

/** How opaque the drawn play button is: 0.5 is half transparent. */
export const PLAY_BUTTON_OPACITY = 0.5;

/** The play button, drawn onto a canvas that already holds the picture. */
export function drawPlayButton(ctx, w, h) {
  const { cx, cy, r, tri } = playButton(w, h);
  const g = ctx.createRadialGradient(cx, cy, r * 0.2, cx, cy, Math.max(w, h) * 0.6);
  g.addColorStop(0, 'rgba(0,0,0,0)'); g.addColorStop(1, 'rgba(0,0,0,.28)');
  ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);                        // a soft vignette, so the button reads on any frame
  /* The button itself — circle, triangle and the shadow under it — is drawn at half
     opacity, so the picture shows through it: a button that sits ON the frame rather
     than hiding the middle of it. */
  ctx.save();
  ctx.globalAlpha = PLAY_BUTTON_OPACITY;
  ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = '#fff'; ctx.shadowColor = 'rgba(0,0,0,.35)'; ctx.shadowBlur = r * 0.35; ctx.fill();
  ctx.shadowBlur = 0;
  ctx.beginPath(); ctx.moveTo(tri[0][0], tri[0][1]); ctx.lineTo(tri[1][0], tri[1][1]); ctx.lineTo(tri[2][0], tri[2][1]); ctx.closePath();
  ctx.fillStyle = '#16191d'; ctx.fill();
  ctx.restore();
}

/** A JPEG of whatever `source` (a video or an image) shows now, up to 1120 px wide. */
export async function thumbnailBlob(source, withButton) {
  const sw = source.videoWidth || source.naturalWidth, sh = source.videoHeight || source.naturalHeight;
  if (!sw || !sh) throw new Error('There is no picture to use yet.');
  const w = Math.min(1120, sw), h = Math.round(sh * (w / sw));
  const c = document.createElement('canvas'); c.width = w; c.height = h;
  const ctx = c.getContext('2d');
  ctx.drawImage(source, 0, 0, w, h);
  if (withButton) drawPlayButton(ctx, w, h);
  return new Promise((ok, no) => c.toBlob(b => b ? ok(b) : no(new Error('The picture could not be made.')), 'image/jpeg', 0.88));
}

const bar = () => {
  const wrap = el('div', 'vl__prog'); const fill = el('i'); wrap.appendChild(fill);
  return { wrap, set: f => { fill.style.width = Math.round(Math.max(0, Math.min(1, f)) * 100) + '%'; } };
};

/** @param {{ onDone: () => void }} opts */
export function videoUploadBox({ onDone }) {
  const box = el('div', 'vl__upload');

  const title = el('input', 'inp'); title.type = 'text'; title.placeholder = 'Title';
  const vfile = el('input', 'vl2drop__in'); vfile.type = 'file'; vfile.accept = 'video/mp4,.mp4';
  const pfile = el('input', 'vl2drop__in'); pfile.type = 'file'; pfile.accept = 'image/jpeg,image/png,image/webp';
  const preview = el('video', 'vl__preview'); preview.controls = true; preview.muted = true; preview.playsInline = true; preview.preload = 'metadata';
  const useFrame = el('button', 'btn btn--sm btn--gh', 'Use this frame as the thumbnail');
  useFrame.type = 'button'; useFrame.disabled = true;
  const buttonBox = el('input'); buttonBox.type = 'checkbox'; buttonBox.checked = true;
  const buttonLbl = el('label', 'chk'); buttonLbl.append(buttonBox, el('span', null, 'Draw a play button on it'));
  const thumbImg = el('img', 'vl__thumb hidden'); thumbImg.alt = 'Thumbnail';
  const info = el('p', 'note', 'Choose the MP4 first. Its length is read from the file.');
  const go = el('button', 'btn btn--pri', 'Upload'); go.type = 'button'; go.disabled = true;
  const cancel = el('button', 'btn btn--gh hidden', 'Cancel'); cancel.type = 'button';
  const pv = bar(), pt = bar();
  const status = el('p', 'note', '');
  /* The first thing on screen is the drop area; the rest appears once a video is chosen. */
  const detail = el('div', 'vl2up hidden');
  const prog = el('div', 'vl2up__prog hidden');
  const drop = el('label', 'vl2drop');
  const dropIcon = el('span', 'vl2drop__ic');
  dropIcon.innerHTML = '<svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M12 16V4M7 9l5-5 5 5"/><path d="M5 20h14"/></svg>';
  const dropText = el('span', 'vl2drop__t', 'Choose an MP4, or drop it here');
  const dropSub = el('span', 'vl2drop__s', 'H.264 video. Its length is read from the file.');
  drop.append(dropIcon, dropText, dropSub, vfile);
  ['dragenter', 'dragover'].forEach(t => drop.addEventListener(t, e => { e.preventDefault(); drop.classList.add('is-over'); }));
  ['dragleave', 'drop'].forEach(t => drop.addEventListener(t, e => { e.preventDefault(); drop.classList.remove('is-over'); }));
  drop.addEventListener('drop', e => {
    const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
    if (!f) return;
    const dt = new window.DataTransfer(); dt.items.add(f); vfile.files = dt.files; vfile.onchange();
  });

  let duration = 0, videoUrl = '', thumb = null, thumbType = '', ctrl = null;
  const ready = () => { go.disabled = !(vfile.files[0] && thumb && title.value.trim() && duration > 0); };

  const field = (label, ...nodes) => { const f = el('div', 'vl__field'); f.appendChild(el('span', 'vl__lbl', label)); f.append(...nodes); return f; };

  vfile.onchange = () => {
    const f = vfile.files[0];
    if (videoUrl) URL.revokeObjectURL(videoUrl);
    duration = 0; thumb = null; thumbImg.classList.add('hidden'); useFrame.disabled = true; preview.removeAttribute('src');
    if (!f) { detail.classList.add('hidden'); dropText.textContent = 'Choose an MP4, or drop it here'; ready(); return; }
    if (!/\.mp4$/i.test(f.name) && f.type !== 'video/mp4') {
      detail.classList.add('hidden'); dropText.textContent = 'That is not an MP4';
      dropSub.textContent = 'Export the video as MP4 (H.264) and choose it again.'; vfile.value = ''; ready(); return;
    }
    detail.classList.remove('hidden'); dropText.textContent = f.name; dropSub.textContent = 'Click to choose a different video';
    videoUrl = URL.createObjectURL(f);
    preview.src = videoUrl;
    if (!title.value.trim()) title.value = titleFromFilename(f.name);
    info.textContent = 'Reading the video…';
    preview.onloadedmetadata = () => {
      duration = preview.duration;
      info.textContent = `${clock(duration)} long · ${(f.size / 1024 / 1024).toFixed(1)} MB. Move to the moment you want and press “Use this frame”, or choose a picture of your own.`;
      useFrame.disabled = false; ready();
    };
    preview.onerror = () => { info.textContent = 'This browser cannot read that video. Is it H.264?'; };
    ready();
  };

  const setThumb = (blob, type) => {
    thumb = blob; thumbType = type;
    thumbImg.src = URL.createObjectURL(blob); thumbImg.classList.remove('hidden'); ready();
  };
  useFrame.onclick = async () => {
    try { const b = await thumbnailBlob(preview, buttonBox.checked); setThumb(b, 'image/jpeg'); }
    catch (e) { toast(e.message, true); }
  };
  pfile.onchange = async () => {
    const f = pfile.files[0]; if (!f) return;
    if (!buttonBox.checked) { setThumb(f, f.type); return; }
    try {                                    // a picture of your own, with the button drawn on it
      const img = new Image(); const u = URL.createObjectURL(f);
      await new Promise((ok, no) => { img.onload = ok; img.onerror = () => no(new Error('That picture could not be read.')); img.src = u; });
      setThumb(await thumbnailBlob(img, true), 'image/jpeg'); URL.revokeObjectURL(u);
    } catch (e) { toast(e.message, true); }
  };
  title.oninput = ready;

  go.onclick = async () => {
    const f = vfile.files[0];
    go.disabled = true; cancel.classList.remove('hidden'); prog.classList.remove('hidden'); ctrl = new AbortController();
    status.textContent = 'Preparing…'; pv.set(0); pt.set(0);
    try {
      const [tu, vu] = await Promise.all([
        vlUploadUrl({ kind: 'thumb', filename: 'thumbnail', content_type: thumbType, size: thumb.size }),
        vlUploadUrl({ kind: 'video', filename: f.name, content_type: 'video/mp4', size: f.size })
      ]);
      status.textContent = 'Uploading the thumbnail…';
      await putFile(tu.url, tu.headers, thumb, pt.set, ctrl.signal); pt.set(1);
      status.textContent = 'Uploading the video…';
      await putFile(vu.url, vu.headers, f, pv.set, ctrl.signal); pv.set(1);
      status.textContent = 'Saving…';
      await vlCreateVideo({ title: title.value, video_path: vu.path, thumb_path: tu.path, duration_s: Math.round(duration * 100) / 100 });
      toast('Video uploaded and added.');
      onDone();
    } catch (e) {
      status.textContent = e.message;
      toast(e.message, true);
      go.disabled = false;
    } finally { cancel.classList.add('hidden'); ctrl = null; }
  };
  cancel.onclick = () => ctrl && ctrl.abort();

  const pick = el('label', 'btn btn--sm btn--gh vl2up__pick');
  pick.append(el('span', null, 'Choose a picture'), pfile);
  const thumbRow = el('div', 'vl__thumbrow');
  thumbRow.append(useFrame, el('span', 'note', 'or'), pick, buttonLbl);
  prog.append(field('Thumbnail', pt.wrap), field('Video', pv.wrap));
  detail.append(
    preview, info,
    field('Title', title),
    field('Thumbnail', thumbRow, thumbImg),
    prog, status
  );
  const row = el('div', 'vl2dlg__act'); row.append(cancel, go); detail.appendChild(row);
  box.append(drop, detail);
  return box;
}
